import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { Box, Container, Link as MuiLink, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import logoImage from '../assets/images/Logo.svg';

const getRoleFromStorageOrPath = (pathname) => {
  const raw = (() => {
    try {
      return localStorage.getItem('role') || '';
    } catch {
      return '';
    }
  })();

  const normalized = String(raw).toLowerCase();
  if (normalized === 'admin' || normalized === 'college_admin') return 'admin';
  if (normalized === 'club_member' || normalized === 'club-member') return 'club_member';
  if (normalized === 'student') return 'student';

  const path = String(pathname || '').toLowerCase();
  if (path.includes('/admin')) return 'admin';
  if (path.includes('clubmember') || path.includes('club-member') || path.includes('club_member')) return 'club_member';
  return 'student';
};

const PostLoginFooter = () => {
  const theme = useTheme();
  const location = useLocation();
  const year = new Date().getFullYear();
  const footerElRef = useRef(null);

  const role = useMemo(
    () => getRoleFromStorageOrPath(location.pathname),
    [location.pathname]
  );

  const links = useMemo(() => {
    const base = [
      { text: 'Privacy', to: '/privacy' },
      { text: 'Terms', to: '/terms' },
    ];

    if (role === 'admin') {
      return [
        { text: 'Dashboard', to: '/admin/dashboard' },
        { text: 'Clubs', to: '/admin/clubs' },
        { text: 'Members', to: '/admin/memberships' },
        ...base,
      ];
    }

    if (role === 'club_member') {
      return [
        { text: 'Events', to: '/events' },
        { text: 'Manage Events', to: '/eventmanagement' },
        { text: 'Announcements', to: '/announcements' },
        ...base,
      ];
    }

    return [
      { text: 'Events', to: '/events' },
      { text: 'Skills', to: '/skills' },
      { text: 'Doubts', to: '/doubts' },
      { text: 'Lost & Found', to: '/lostfound' },
      ...base,
    ];
  }, [role]);

  const colors = {
    primary: '#0D1C4F',
    text: '#FFFFFF',
    textSecondary: 'rgba(255,255,255,0.75)',
    bg: '#111827',
    hover: '#016f57',
  };

  useLayoutEffect(() => {
    const el = footerElRef.current;
    if (!el) return;

    const setVar = () => {
      const h = Math.ceil(el.getBoundingClientRect().height || 0);
      document.documentElement.style.setProperty('--post-login-footer-height', `${h}px`);
    };

    setVar();

    if (typeof ResizeObserver === 'undefined') {
      // Fallback: best-effort update on resize
      window.addEventListener('resize', setVar);
      return () => {
        window.removeEventListener('resize', setVar);
        document.documentElement.style.setProperty('--post-login-footer-height', '0px');
      };
    }

    const ro = new ResizeObserver(() => setVar());
    ro.observe(el);

    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty('--post-login-footer-height', '0px');
    };
  }, [role]);

  return (
    <Box
      component="footer"
      ref={footerElRef}
      id="post-login-footer"
      data-post-login-footer="true"
      sx={{
        mt: 'auto',
        width: '100%',
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        backgroundColor: alpha(colors.bg, 0.95),
        backdropFilter: 'blur(10px)',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 2, md: 3 }}
          sx={{ py: { xs: 1.5, sm: 2 } }}
        >
          {/* Brand */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={{ xs: 'center', md: 'flex-start' }}
            spacing={1.25}
            sx={{ minWidth: 0 }}
          >
            <Box
              component="img"
              src={logoImage}
              alt="PeerCampus"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
              sx={{ height: { xs: 24, sm: 28 }, width: 'auto', flex: '0 0 auto' }}
            />

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1, color: colors.text }}
                noWrap
              >
                PeerCampus
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: colors.textSecondary, display: { xs: 'none', sm: 'block' } }}
                noWrap
              >
                Signed-in experience
              </Typography>
            </Box>
          </Stack>

          {/* Links */}
          <Box
            component="nav"
            aria-label="Footer"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: { xs: 'center', md: 'center' },
              rowGap: { xs: 0.75, sm: 1 },
              '& a:not(:first-of-type)::before': {
                content: '"·"',
                display: 'inline-block',
                margin: { xs: '0 6px', sm: '0 10px' },
                color: alpha(colors.text, 0.28),
                fontWeight: 700,
              },
            }}
          >
            {links.map((l) => (
              <MuiLink
                key={l.text}
                component={RouterLink}
                to={l.to}
                underline="none"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: colors.textSecondary,
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  fontWeight: 600,
                  lineHeight: 1.25,
                  px: 0.25,
                  py: 0.25,
                  borderRadius: 1,
                  whiteSpace: 'nowrap',
                  transition: 'background-color 120ms ease, color 120ms ease',
                  '&:hover': {
                    color: colors.text,
                    backgroundColor: alpha(colors.text, 0.06),
                  },
                  '&:focus-visible': {
                    outline: `3px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {l.text}
              </MuiLink>
            ))}
          </Box>

          {/* Meta: only copyright */}
          <Stack
            direction={{ xs: 'row', md: 'row' }}
            alignItems="center"
            justifyContent={{ xs: 'center', md: 'flex-end' }}
            spacing={1.25}
          >
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              © {year} PeerCampus
            </Typography>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default PostLoginFooter;
